/**
 * Mobile navigation drawer: accessible open/close with focus trap,
 * focus return, ESC-to-close and body scroll lock. No external menu
 * library (docs/CLAUDE.md — vanilla JS, progressive enhancement).
 *
 * Markup contract (see template-parts/header/mobile-menu.php):
 *   <button data-menu-toggle aria-controls="rsv-mobile-menu" aria-expanded="false">
 *   <div id="rsv-mobile-menu" class="rsv-mobile-menu" hidden>
 *     <button data-menu-close>...</button>
 *     ...focusable nav links...
 *   </div>
 */

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function initMobileNavigation() {
  const toggle = document.querySelector('[data-menu-toggle]');
  const menu = document.querySelector('[data-menu-panel]');

  if (!toggle || !menu) {
    return;
  }

  const closeButtons = menu.querySelectorAll('[data-menu-close]');
  let lastFocusedElement = null;

  const isOpen = () => !menu.hasAttribute('hidden');

  function openMenu() {
    lastFocusedElement = document.activeElement;
    menu.removeAttribute('hidden');
    toggle.setAttribute('aria-expanded', 'true');
    document.body.classList.add('rsv-scroll-locked');
    document.addEventListener('keydown', handleKeydown);

    const firstFocusable = menu.querySelector(FOCUSABLE_SELECTOR);
    if (firstFocusable instanceof HTMLElement) {
      firstFocusable.focus();
    }
  }

  function closeMenu() {
    menu.setAttribute('hidden', '');
    toggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('rsv-scroll-locked');
    document.removeEventListener('keydown', handleKeydown);

    if (lastFocusedElement instanceof HTMLElement) {
      lastFocusedElement.focus();
    } else {
      toggle.focus();
    }
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') {
      closeMenu();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusable = Array.from(menu.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => el.offsetParent !== null
    );

    if (focusable.length === 0) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  toggle.addEventListener('click', () => {
    if (isOpen()) {
      closeMenu();
    } else {
      openMenu();
    }
  });

  closeButtons.forEach((button) => {
    button.addEventListener('click', closeMenu);
  });

  menu.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', closeMenu);
  });

  window.addEventListener('resize', () => {
    if (isOpen() && window.innerWidth >= 1024) {
      closeMenu();
    }
  });
}
